import { call, put, select } from 'redux-saga/effects';

import { differenceInCalendarDays } from 'date-fns';

import {
  ClientInterface,
  ClientPrice,
  SubsidiaryInterface,
  ConfigurationInterface,
  ReasonInterface,
  ClientBillingInterface,
  PromotionInterface,
  UserInterface,
  OrderTypeInterface,
  TributacaoInterface
} from '@e3labs/isa-data-dictionary';

import api from '~/services/api';
import orderService from './order-service';
import productService from '~/store/modules/product/product-service';
import notification from '~/services/notification';
import promotionService from '../promotion/promotion-service';

import { REASON_ACTIONS, REASON_TYPE, SHIPPING_TYPE, TENANT_ID } from '~/constants';

import { ProductStateListInterface } from '../product/product-interface';
import {
  CheckBlockOrderInterface,
  OrderItemCreateInterface,
  OrderRecalculateProps,
  ProductListBonificationRestrictionResponse,
  SetPaymentOptionsAndPlansInterface,
  OrderCreateInterface
} from './order-interface';

import { formatDate, formatMessage } from '~/i18n/translate';

import { AppStateType } from '../root-reducer';

import { toDecimals } from '~/utils/format';
import { getAsyncPriceTable } from './order-create-sagas';
import { getTax } from '../tributacao/tributacao-service';
import { UserAuthInterface } from '../auth/auth-interface';

export const sort = (items: any, sortString: Boolean = true, sortByName?: boolean) => {
  if (sortByName) {
    return [...items]?.sort((a: any, b: any) => {
      const field = sortByName ? 'name' : 'externalCode';

      // Função para normalizar (remover caracteres especiais e lidar com números)
      const normalize = (value: string) =>
        value
          .replace(/^[^a-zA-Z0-9]+/, '') // Remove caracteres especiais no início
          .toUpperCase(); // Ignora diferença de maiúsculas e minúsculas

      // Normalizar os valores para comparação
      const valueA = normalize(a[field] || '');
      const valueB = normalize(b[field] || '');

      // Primeiro, compara pelo nome
      const nameComparison = valueA.localeCompare(valueB, undefined, { numeric: true });

      // Se os nomes forem iguais, compara pelo externalCode
      if (nameComparison !== 0) {
        return nameComparison;
      } else {
        const externalCodeA = a.externalCode || '';
        const externalCodeB = b.externalCode || '';
        return externalCodeA.localeCompare(externalCodeB, undefined, { numeric: true });
      }
    });
  }

  return [...items]?.sort((a: any, b: any) =>
    sortString ? ('' + a.externalCode).localeCompare(b.externalCode) : a.externalCode - b.externalCode
  );
};

export const getLoadTypes = (items, shippingType) => {
  return (
    [...items]?.filter(
      x =>
        x.deliveryType ===
          (shippingType === SHIPPING_TYPE.PAID ? 'CIF' : shippingType === SHIPPING_TYPE.FREE ? 'FOB' : 'BNF') ||
        x.deliveryType === ''
    ) ?? []
  );
};

export const getUserSubsidiaries = (subsidiaries: SubsidiaryInterface[], userSubsidiaries: string[]) => {
  return subsidiaries?.filter(subsidiary => userSubsidiaries.includes(subsidiary?.externalCode)) ?? [];
};

export const getSubsidiaryStocks = (subsidiaries: SubsidiaryInterface[], subsidiary: SubsidiaryInterface) => {
  return [...subsidiaries?.filter(item => subsidiary?.subsidiaryStock?.includes(item.externalCode))];
};

export const getPriceTable = (items: ClientPrice[], subsidiary: SubsidiaryInterface, client: ClientInterface) => {
  const clientPrice: any =
    [...items]?.find(
      item =>
        item.subsidiaryExternalCode === subsidiary?.externalCode && item?.clientExternalCode === client?.externalCode
    ) ?? null;

  return clientPrice ? clientPrice?.priceTableCode : client?.priceTableCode;
};

export const getReasons = async (
  configuration: ConfigurationInterface,
  selects: ReasonInterface[],
  client: ClientInterface,
  subsidiary: SubsidiaryInterface
) => {
  let blockingReasons = {};

  const blockOrder = selects?.some(x => x?.actions?.orders === REASON_ACTIONS.DENY);
  const anlyCashAllowedOrder = selects?.some(x => x?.actions?.orders === REASON_ACTIONS.ANLY_CASH_ALLOWED);
  const blockSubsidiary = selects?.some(x => x?.subsidiaryExternalCode);
  const blockSubsidiaries = selects?.some(x => x?.subsidiariesExternalCodes?.length > 0);
  const blockForVisitDate = selects.some(x => x.type === REASON_TYPE.VISIT_DATE);

  if (blockOrder) {
    if (client?.blocked) {
      const reason = selects?.find(x => x?.externalCode === (client?.blockReason ?? configuration?.blockReason));

      blockingReasons = {
        ...blockingReasons,
        [REASON_TYPE.CLIENTE_BLOCK]: reason
      };
    }

    if (blockSubsidiary) {
      const reason = selects?.find(x => x.subsidiaryExternalCode === subsidiary?.externalCode);

      blockingReasons = {
        ...blockingReasons,
        [REASON_TYPE.SUBSIDIARY_BLOCKED]: reason
      };
    }

    if (blockForVisitDate) {
      if (client?.nextVisit && formatDate(new Date(client?.nextVisit?.dateNextVisit)) !== formatDate(new Date())) {
        const visitDate = selects?.find(x => x.type === REASON_TYPE.VISIT_DATE) ?? null;

        blockingReasons = {
          ...blockingReasons,
          [REASON_TYPE.VISIT_DATE]: visitDate
        };
      }
    }

    if (blockSubsidiaries) {
      const reason = selects?.find(x => x.subsidiariesExternalCodes?.includes(subsidiary?.externalCode));

      blockingReasons = {
        ...blockingReasons,
        [REASON_TYPE.SALES_RESTRICTION]: reason
      };
    }

    const { data: billings }: { data: ClientBillingInterface[] } = await api.get(
      `/client/billing?clientExternalCode=${client?.externalCode}`
    );

    const nonPayment =
      billings
        ?.map(billing => {
          const diff = differenceInCalendarDays(new Date(billing.dueDate), new Date());

          return {
            diff,
            late: diff < 0 ? true : false,
            ...billing
          };
        })
        ?.filter((billing: any) => billing?.late === true && !billing?.paymentDate)?.length ?? 0;

    if (nonPayment > 0) {
      const reasonNonPayment = selects?.find(reason => reason?.type === REASON_TYPE.NON_PAYMENT) ?? null;
      blockingReasons = {
        ...blockingReasons,
        [REASON_TYPE.NON_PAYMENT]: {
          ...reasonNonPayment,
          countNonPayment: nonPayment
        }
      };
    }
    if (client?.availableCredit <= 0) {
      const otherReason = selects?.find(reason => reason.type === REASON_TYPE.CREDIT_EXCEEDED) ?? null;
      blockingReasons = {
        ...blockingReasons,
        [REASON_TYPE.CREDIT_EXCEEDED]: otherReason
      };
    }
  }

  if (anlyCashAllowedOrder) {
    if (blockSubsidiaries) {
      const reason = selects?.find(x => x.subsidiariesExternalCodes?.includes(subsidiary?.externalCode));

      blockingReasons = {
        ...blockingReasons,
        [REASON_TYPE.SALES_RESTRICTION]: reason
      };
    }
  }

  const reasons = Object.keys(blockingReasons).length === 0 ? selects : Object.values(blockingReasons);

  return { reasons, blockingReasons };
};

export const getMargin = (soldPrice: number, financialCost: number, decimals?: number) => {
  const marginValue = soldPrice - financialCost;
  const marginPercentage = soldPrice > 0 ? toDecimals((marginValue / soldPrice) * 100, decimals ?? 2) : 0;

  const margin = {
    marginValue,
    marginPercentage
  };

  return margin;
};

export const getPriceDifference = (orderItem: OrderItemCreateInterface, decimals?: number) => {
  const typeBalance = orderItem?.promotion?.typeBalance;
  const debitBalance = orderItem?.promotion?.debitBalance;
  const minimumPrice = orderItem?.minimumPrice;

  let priceDifference;

  if (orderItem.soldPrice >= orderItem.baseValue || !typeBalance) {
    priceDifference = (orderItem?.soldPrice - orderItem?.baseValue) * orderItem?.soldQuantity;
  } else if (typeBalance) {
    if (debitBalance) {
      priceDifference = (orderItem?.soldPrice - orderItem?.baseValue) * orderItem?.soldQuantity;
    } else {
      priceDifference = (orderItem?.soldPrice - minimumPrice) * orderItem?.soldQuantity;
    }
  }

  return toDecimals(priceDifference, decimals ?? 2);
};

export const isBlockedProduct = (orderItem: OrderItemCreateInterface, acceptNegativeBalance?: boolean) => {
  if (acceptNegativeBalance) {
    return false;
  }

  if (orderItem.isAboveDiscountAllowed) {
    return true;
  } else if (orderItem.isBelowMinimumPrice) {
    return true;
  } else if (
    orderItem.promotion &&
    !orderItem.promotion?.typeBalance &&
    orderItem.totalPrice < (orderItem.promotion?.promotionalPrice ?? 0)
  ) {
    return true;
  } else {
    return false;
  }
};

// export const comparePrices = (total: any, minimum: any, configuration) => {
//   if (configuration.decimals > 2) {
//     const difference = Number(total) < Number(minimum);

//     console.trace('comparePrices', {
//       total,
//       minimum,
//       difference
//     });

//     return difference;
//   } else {
//     const regex = /(\d+)(\.\d{1,2})?/;
//     const minPriceRegex = String(minimum).match(regex);
//     const priceRegex = String(total).match(regex);
//     const difference2 = Number(priceRegex?.[0] ?? 0) < Number(minPriceRegex?.[0] ?? 0);

//     console.trace('comparePrices 2', {
//       total,
//       minimum,
//       difference2
//     });

//     return difference2;
//   }
// };

export const comparePrices = (total: any, minimum: any, decimals?: number) => {
  const parsedTotal = toDecimals(total, decimals ?? 2);
  const parsedMinimum = toDecimals(minimum, decimals ?? 2);

  const isBelowMinimum = parsedTotal < parsedMinimum;

  return isBelowMinimum;
};

export function updateTotalizers({
  orderItems,
  orderDetails,
  precision = 2,
  tenantId
}: {
  orderItems;
  orderDetails;
  precision?: number;
  tenantId?: string;
}) {
  if (orderItems.length === 0) {
    return {
      productsTotal: 0,
      productsTotalBonificated: 0,
      productsQuantity: 0,
      productsTributos: 0,
      financialCostTotal: 0,
      totalQuantity: 0
    };
  }
  let productQuantity = [...new Set(orderItems.map(i => i?.externalCode))].length;

  const itemsPrices = orderItems
    .filter(item => !item?.promotion?.isPromotionGroup || !TENANT_ID.SELMI.includes(tenantId))
    .map(item => ({
      soldQuantity: item.soldQuantity,
      totalPrice: toDecimals(item?.soldPrice * +item.soldQuantity, precision),
      totalBonificated: item.bonificated ? item?.soldPrice * +item.soldQuantity : 0,
      vlTributos: item?.tributacao ? item.tributacao.vlTributos : 0,
      financialCost: item.financialCost
    }));

  const { productsTotal, productsTotalBonificated, soldQuantity, productsTributos, financialCostTotal } =
    itemsPrices.reduce(
      (prev, cur) => ({
        productsTotal: toDecimals(prev.productsTotal + cur.totalPrice, precision),
        productsTotalBonificated: toDecimals(prev.productsTotalBonificated + cur.totalBonificated, precision),
        soldQuantity: prev.soldQuantity + cur.soldQuantity,
        productsTributos: toDecimals(prev.productsTributos + cur.vlTributos, precision),
        financialCostTotal: toDecimals(prev.financialCostTotal + cur.financialCost * cur.soldQuantity, precision)
      }),
      {
        productsTotal: 0,
        productsTotalBonificated: 0,
        soldQuantity: 0,
        productsTributos: 0,
        financialCostTotal: 0
      }
    );
  return {
    totalPrice: productsTotal + (orderDetails?.shippingFee ?? 0),
    productQuantity,
    productsTotalBonificated,
    totalQuantity: soldQuantity,
    productsTributos,
    financialCostTotal
  };
}

export function* recalculatePrices(payload: OrderRecalculateProps, allowPriceUpdate = true, showAlert = false) {
  const configuration: ConfigurationInterface = yield select((state: AppStateType) => state.configuration);
  const user: UserAuthInterface = yield select((state: AppStateType) => state.auth.user);
  const { paymentPlan, paymentOption } = yield select((state: AppStateType) => state?.order?.form?.data.orderDetails);

  const { client, subsidiary, subsidiaryStock, paymentByGroup, orderItems, shippingType } = payload;

  if (!payload.orderItems || !payload.paymentPlan || !payload.paymentOption || !paymentPlan) return [];

  const { onlyWithStock, onlyWithPromotion, orderBy, clientMix }: ProductStateListInterface = yield select(
    (state: AppStateType) => state.product.list
  );

  const externalCodes = orderItems.map(item => {
    if (item?.externalCode) {
      return item?.externalCode;
    }
  });
  const priceTableCode = yield call(getAsyncPriceTable, subsidiary, client);
  const filters = {
    clientExternalCode: client?.externalCode ?? null,
    subsidiaryExternalCode: subsidiary?.externalCode ?? null,
    governmentalCode: client?.governmentalCode ?? null,
    applyTax: client?.applyTax ? client?.applyTax : null,
    applyTax2: configuration?.taxation ? client?.applyTax2 : null,
    applyTaxST: configuration?.taxation ? client?.applyTaxST : null,
    finalConsumer: client?.finalConsumer ?? null,
    subsidiaryStockExternalCode: subsidiaryStock?.externalCode ?? null,
    usePaymentPlansGroup: client?.usePaymentPlansGroup,
    paymentPlanExternalCode: client?.usePaymentPlansGroup
      ? [paymentByGroup?.paymentPlan1?.externalCode, paymentByGroup?.paymentPlan2?.externalCode].join(',')
      : paymentPlan?.externalCode,
    paymentOption: payload?.paymentOption?.externalCode ?? paymentOption?.externalCode,
    priceTableCode,
    subsidiaryStock: subsidiaryStock?.subsidiaryStock ?? null,
    productExternalCodes: externalCodes.toString(),
    onlyWithStock,
    onlyWithPromotion,
    clientMix,
    orderBy: orderBy[0],
    orderAsc: orderBy[1],
    taxation: configuration?.taxation,
    state: client?.commercialInfo?.state ?? null
  };

  const { data: _products } = yield call(orderService.getOrderProducts, {
    filters: {
      ...filters
    }
  });

  const products = _products.map(product => ({
    ...product,
    baseValue: product?.baseValue
  }));

  if (showAlert && products?.length === 0) {
    return;
  }

  if (showAlert && products?.length !== externalCodes?.length) {
    return;
  }

  let items: OrderItemCreateInterface[] = [];

  for (const oldItem of orderItems) {
    let product = products.find(x => x?.externalCode === oldItem?.externalCode);
    if (!product) {
      continue;
    }

    const packing = oldItem?.packing ?? product?.packing ?? null;
    const packingQuantity = packing?.packingQuantity ?? 1;
    const quantity = oldItem?.soldQuantity;

    const baseValue = toDecimals(product?.baseValue ?? oldItem?.baseValue, configuration?.decimals ?? 2);

    let promotion: any | null = null;
    let promotions = oldItem?.promotions || [];
    let oldPromotion: any = null;
    let soldPrice = allowPriceUpdate ? baseValue * (packing?.packingQuantity ?? 1) : oldItem.soldPrice;

    if (
      oldItem?.promotion?.externalCode ||
      oldItem?.promotion?.codpromocaomed ||
      oldItem?.promotion?.aggregatorExternalCode
    ) {
      let availablePromotions = promotions ?? [];

      if (!availablePromotions.length) {
        const { data } = yield call(promotionService.getAvailablePromotions, {
          client,
          paymentPlanExternalCode: paymentPlan?.externalCode,
          priceTableCode: client?.priceTableCode,
          subsidiaryExternalCode: subsidiary?.externalCode,
          product,
          orderType: payload?.orderType,
          products
        });

        availablePromotions = data ?? [];
      }
      if (availablePromotions?.length > 0) {
        const validPromotions = availablePromotions.filter(
          promotion =>
            (!promotion?.initialQuantity && !promotion?.finalQuantity) ||
            (!promotion?.initialQuantity && quantity <= promotion?.finalQuantity) ||
            (quantity >= promotion?.initialQuantity && !promotion?.finalQuantity) ||
            (quantity >= promotion?.initialQuantity && quantity <= promotion?.finalQuantity)
        );

        validPromotions.sort((a, b) => {
          if (a.minimumPrice > b.minimumPrice) return 1;
          if (a.minimumPrice < b.minimumPrice) return -1;
          return 0;
        });

        let selectedPromotion = validPromotions.find(
          promotion => promotion.externalCode === oldItem?.promotion?.promotionExternalCode
        );

        if (!selectedPromotion) {
          selectedPromotion = validPromotions.find(promotion => promotion.instantApplyDiscount);
        }

        promotion = selectedPromotion
          ? promotionService.buildPromotion(
              {
                promotion: selectedPromotion,
                product,
                soldPrice,
                soldQuantity: quantity
              },
              configuration?.decimals ?? 2
            )
          : null;
      }
    }

    if (showAlert && !promotion) {
      notification.warning(
        configuration.blockSaleWithoutPromotion
          ? `Produto ${product?.externalCode} - ${product?.name} não existe promoção vinculada e será removido.`
          : `Produto ${product?.externalCode} - ${product?.name} não existe promoção vinculada.`
      );

      if (configuration.blockSaleWithoutPromotion) {
        continue;
      }
    }

    if (
      showAlert &&
      oldItem?.promotion?.promotionExternalCode &&
      promotion?.externalCode !== oldItem?.promotion?.promotionExternalCode
    ) {
      notification.warning(
        `O preço do produto ${product?.externalCode} - ${product?.name} foi modificado.\nA promoção código ${oldItem?.promotion?.promotionExternalCode} não é mais válida.`
      );
    }

    const hasPromotion =
      !!promotion &&
      ((promotion?.externalCode || promotion?.promotion?.externalCode) ===
        (oldItem?.promotion?.promotionExternalCode || oldItem?.promotion?.externalCode) ||
        promotion?.active);
    let discountPercentage = oldItem?.discountPercentage ? oldItem?.discountPercentage : 0;

    if (allowPriceUpdate) {
      if (
        (!promotion && discountPercentage) ||
        (hasPromotion && discountPercentage > (promotion?.discountPercentage ?? 0))
      ) {
        soldPrice =
          toDecimals(baseValue - (baseValue * discountPercentage) / 100, configuration?.decimals ?? 2) *
          packingQuantity;
      } else if (hasPromotion && promotion?.promotionalPrice) {
        soldPrice = promotion.promotionalPrice * packingQuantity;
        discountPercentage = promotion.discountPercentage ?? promotion?.promotion?.discountPercentage;
      }
    } else {
      if (
        (!oldPromotion && discountPercentage) ||
        (hasPromotion && discountPercentage !== (oldPromotion?.discountPercentage ?? 0))
      ) {
        soldPrice =
          toDecimals(baseValue - (baseValue * discountPercentage) / 100, configuration?.decimals ?? 2) *
          packingQuantity;
      } else if (hasPromotion && oldPromotion?.promotionalPrice) {
        discountPercentage = oldPromotion?.discountPercentage ?? 0;
        soldPrice = oldPromotion?.promotionalPrice * packingQuantity;
      }
    }

    if (configuration?.useProfitControl && promotion?.includeFinancialPercentage) {
      const percentualRate = paymentPlan?.financialPercentageRate;

      const promotionCost = promotion?.financialCost ?? 0;

      product.stock = {
        ...product.stock,
        financialCost: promotionCost + (promotionCost * percentualRate) / 100
      };
    }
    const makePromotion = promotionService.buildPromotion(
      {
        promotion: promotion?.promotion ?? null,
        product,
        soldPrice,
        soldQuantity: quantity
      },
      configuration
    );

    const maxDiscountPercentage = product?.maxDiscountPercentage ?? product?.price?.maxDiscountPercentage ?? 0;

    let minimumPrice = oldItem?.minimumPrice ?? baseValue;
    let maximumPrice = oldItem?.maximumPrice ?? baseValue + (baseValue * configuration?.maximumSalePercentage) / 100;

    if (promotion && promotion?.active && promotion?.minimumPrice) {
      minimumPrice = promotion?.minimumPrice ?? 0;
    } else if (maxDiscountPercentage > 0) {
      minimumPrice = oldItem?.minimumPrice ?? baseValue - (baseValue * maxDiscountPercentage) / 100;
    }

    const newItem = {
      ...oldItem,
      packingInfos: {
        ...(packing && ((Array.isArray(packing) && packing.length > 0) || !Array.isArray(packing))
          ? {
              unit: packing?.packingUnit,
              type: packing?.packingType,
              availableQuantity: toDecimals(
                product?.availableQuantity /
                  ((Array.isArray(packing) ? packing[0]?.packingQuantity : packing?.packingQuantity) || 1),
                2
              ),
              barCode: packing?.barCode
            }
          : {
              unit: product?.unit,
              type: product?.unit,
              availableQuantity: product?.availableQuantity,
              barCode: product?.barCode
            }),
        packingPrice: baseValue,
        basePackingPrice: baseValue,
        unitaryValue: baseValue
      },
      packing,
      multiplesSubsidiary: product?.multiplesSubsidiary ?? oldItem?.multiplesSubsidiary,
      barCode: packing?.barCode ?? product?.barCode ?? '0',
      enableToEdit: true,
      productExternalCode: product?.externalCode,
      priorityPolice: false,
      discount: oldItem?.discount ?? false,
      isSponsored: oldItem?.isSponsored ?? false,
      isRecommendation: oldItem?.isRecommendation ?? false,
      isBelowMinimumPrice: checkProductIsBelowMinimumPrice({
        isPromotionKit: oldItem?.promotion?.isPromotionKit,
        soldPrice,
        minimumPrice,
        acceptNegativeBalance: configuration?.acceptNegativeBalance,
        decimals: configuration?.decimals
      }),
      minimumPrice,
      maximumPrice,
      totalPrice: toDecimals(quantity * soldPrice, configuration?.decimals ?? 2),
      quantity,
      baseValue: toDecimals(baseValue, configuration?.decimals ?? 2),
      soldQuantity: quantity,
      soldPrice: toDecimals(soldPrice, configuration?.decimals ?? 2),
      discountPercentage,
      financialCost: toDecimals(product?.stock ? product?.stock?.financialCost : 0, configuration?.decimals ?? 2),
      tabulatedPrice: toDecimals(product?.tabulatedPrice, configuration?.decimals ?? 2),
      promotion: {
        ...makePromotion,
        ...makePromotion?.promotion
      },
      tributacao: oldItem?.tributacao,
      vlTributos: 0,
      taxPercentage: product?.tributacao?.taxPercentage ?? 0,
      bonificated: oldItem?.bonificated,
      cartId: oldItem.cartId
    };

    if (configuration?.taxation) {
      const tributacao = getTax({
        client,
        configuration,
        paymentPlan,
        product,
        shippingType,
        soldPrice: toDecimals(soldPrice, configuration?.decimals ?? 2),
        soldQuantity: quantity,
        tenantID: user.tenantID,
        tributacao: (product?.tributacao?.data ?? newItem.tributacao?.data) as TributacaoInterface,
        vendorDiscountPercentage: discountPercentage
      });

      //@ts-ignore
      newItem.tributacao = tributacao;
      newItem.vlTributos = tributacao.vlTributos;
    }

    items.push(newItem);
  }
  return items;
}

export function validateMargin({
  orderItem,
  promotion,
  tenantID,
  orderItems,
  configuration,
  paymentPlan
}: {
  orderItem: any;
  promotion?: PromotionInterface | null | undefined;
  orderItems: any;
  tenantID: any;
  configuration: any;
  paymentPlan: any;
}) {
  let financialCost = promotion?.financialCost ?? 0;
  if (financialCost === 0) {
    financialCost = toDecimals(orderItem?.stock?.financialCost, configuration?.decimals ?? 2);
  }

  if (promotion?.includeFinancialPercentage) {
    financialCost = financialCost + (financialCost * orderItem?.percentualRate) / 100;
  }

  const buildItemToTotalizers = {
    productExternalCode: orderItem?.externalCode ?? orderItem?.externalCode ?? null,
    soldPrice: toDecimals(orderItem?.soldPrice, configuration?.decimals ?? 2) ?? 0,
    soldQuantity: +orderItem?.soldQuantity ? +orderItem?.soldQuantity : +orderItem?.quantity ?? 0,
    total: toDecimals(
      toDecimals(promotion?.promotionalPrice ?? orderItem?.soldPrice, configuration?.decimals ?? 2) *
        (+orderItem?.soldQuantity ? +orderItem?.soldQuantity : +orderItem?.quantity ?? 0),
      2
    ),
    totalBonificated: orderItem?.bonificated
      ? toDecimals(orderItem?.soldPrice, configuration?.decimals ?? 2) *
        (+orderItem?.soldQuantity ? +orderItem?.soldQuantity : +orderItem?.quantity ?? 0)
      : 0,
    vlTributos:
      toDecimals(
        (+orderItem?.soldQuantity ? +orderItem?.soldQuantity : +orderItem?.quantity ?? 0) *
          orderItem?.tributacao?.values?.vlST,
        configuration?.decimals ?? 2
      ) ?? 0,
    financialCost: toDecimals(financialCost, configuration?.decimals ?? 2) ?? 0,
    promotion,
    product: orderItem
  };

  const parsedOrderItems =
    orderItems?.length > 0
      ? orderItems.map(item => ({
          productExternalCode: item?.externalCode,
          soldPrice:
            toDecimals(item?.promotion?.promotionalPrice ?? item?.soldPrice, configuration?.decimals ?? 2) ?? 0,
          soldQuantity: +item?.soldQuantity ?? 0,
          total: toDecimals(item?.soldPrice, configuration?.decimals ?? 2) * +item?.soldQuantity ?? 0,
          totalBonificated: item?.bonificated
            ? toDecimals(item?.soldPrice, configuration?.decimals ?? 2) * +item?.soldQuantity
            : 0,
          vlTributos:
            toDecimals(item?.soldQuantity * item?.tributacao?.values?.vlST, configuration?.decimals ?? 2) ?? 0,
          financialCost:
            toDecimals(item?.promotion?.financialCost ?? item?.stock?.financialCost, configuration?.decimals ?? 2) ?? 0,
          promotion: item?.promotion ?? undefined,
          product: item
        }))
      : [];

  const _totalizers = totalizers({
    orderItems:
      parsedOrderItems?.length > 0
        ? parsedOrderItems?.findIndex(item => item?.externalCode === orderItem?.externalCode) === -1
          ? [...parsedOrderItems, buildItemToTotalizers]
          : parsedOrderItems?.map(item => {
              if (item?.externalCode === orderItem?.externalCode) {
                return buildItemToTotalizers;
              }

              return item;
            })
        : [buildItemToTotalizers],
    precision: configuration?.decimals ?? 2,
    tenantId: tenantID
  });

  const marginControlItems = marginControl({
    configuration,
    financialCostTotal: _totalizers.financialCostTotal,
    productsTotal: _totalizers.productsTotal,
    productsTotalBonificated: _totalizers.productsTotalBonificated
  });

  const parsed = {
    ...marginControlItems,
    isPromotionGroup: isPromotionGroupValidator(promotion) ?? false
  };

  return parsed;
}

export const isPromotionGroupValidator = (promotion?: PromotionInterface | null | undefined): boolean => {
  if (!promotion) {
    return false;
  } else if (promotion?.brandExternalCode) {
    return true;
  } else if (promotion?.sectionExternalCode) {
    return true;
  } else if (promotion?.categoryExternalCode) {
    return true;
  } else if (promotion?.departmentExternalCode) {
    return true;
  } else if (promotion?.providerExternalCode) {
    return true;
  } else if (promotion?.productGroupExternalCode) {
    return true;
  } else {
    return false;
  }
};

export const marginControl = ({
  productsTotal,
  productsTotalBonificated,
  financialCostTotal,
  configuration
}: {
  configuration?: ConfigurationInterface;
  productsTotal: number;
  productsTotalBonificated: number;
  financialCostTotal: number;
}) => {
  let minimumMargin = configuration?.minimumMargin ?? 0;
  const productWithoutBonificated = toDecimals(productsTotal - productsTotalBonificated, configuration?.decimals ?? 2);

  const marginCalc = configuration?.marginControl
    ? configuration?.marginControl?.filter(
        margin => productWithoutBonificated >= margin?.initialValue && productWithoutBonificated <= margin?.finalValue
      )
    : [];

  if (marginCalc?.length > 0) {
    minimumMargin = marginCalc[0].marginValue;
  }

  const _marginControl = getMarginTotals({
    allowProfitabilityMarkup: configuration?.allowProfitabilityMarkup,
    financialCost: financialCostTotal,
    total: productWithoutBonificated,
    precision: configuration?.decimals
  });

  const marginControl = {
    minimumMargin,
    marginValue: _marginControl.marginValue,
    marginPercentage: productWithoutBonificated > 0 ? _marginControl.marginPercentage : 0,
    marginIsBellowMargin:
      ((productWithoutBonificated - financialCostTotal) / productWithoutBonificated) * 100 < minimumMargin
  };

  return marginControl;
};

export const totalizers = ({
  orderItems,
  precision,
  tenantId
}: {
  orderItems: any;
  precision: number;
  tenantId: string;
}) => {
  if (orderItems.length === 0) {
    return {
      productsTotal: 0,
      productsTotalBonificated: 0,
      productsQuantity: 0,
      productsTributos: 0,
      financialCostTotal: 0,
      productQuantity: 0
    };
  }
  const productQuantity = [...new Set(orderItems.map(i => i?.productExternalCode))].length;

  const itemsPrices = orderItems
    .filter(item => !item?.promotion?.isPromotionGroup || !TENANT_ID.SELMI.includes(tenantId))
    ?.map(item => ({
      soldQuantity: item.soldQuantity,
      total: item.soldPrice * +item.soldQuantity,
      totalBonificated: item.bonificated ? item.soldPrice * +item.soldQuantity : 0,
      vlTributos: item.vlTributos ?? 0,
      financialCost: item?.financialCost
    }));

  const { productsTotal, productsTotalBonificated, productsQuantity, productsTributos, financialCostTotal } =
    itemsPrices.reduce(
      (prev, cur) => ({
        productsTotal: toDecimals(prev.productsTotal + cur.total, precision),
        productsTotalBonificated: toDecimals(prev.productsTotalBonificated + cur.totalBonificated, precision),
        productsQuantity: prev.productsQuantity + cur.soldQuantity,
        productsTributos: toDecimals(prev.productsTributos + cur.vlTributos, precision),
        financialCostTotal: toDecimals(prev.financialCostTotal + cur.financialCost * cur.soldQuantity, precision)
      }),
      {
        productsTotal: 0,
        productsTotalBonificated: 0,
        productsQuantity: 0,
        productsTributos: 0,
        financialCostTotal: 0
      }
    );

  return {
    productsTotal,
    productQuantity,
    productsTotalBonificated,
    productsQuantity,
    productsTributos,
    financialCostTotal
  };
};

export const validateMultiplesSubsidiariesProduct = ({ product, orderItem, configuration }) => {
  if (orderItem?.packing && !configuration?.allowsMultipleWithoutPackaging) {
    return orderItem;
  }

  if (product?.multiplesSubsidiary?.subsidiaryExternalCode) {
    const quantityMultiple = product?.multiplesSubsidiary?.quantityMultiple;
    const newSoldQuantity = Math.ceil(orderItem.soldQuantity / (quantityMultiple ?? 1)) * (quantityMultiple ?? 1);

    if (orderItem?.soldQuantity % (quantityMultiple ?? 1) !== 0) {
      const newOrderItem = {
        ...orderItem,
        soldQuantity: newSoldQuantity,
        totalPrice: toDecimals(orderItem?.soldPrice * newSoldQuantity, configuration?.decimals ?? 2)
      };

      return newOrderItem;
    } else {
      return orderItem;
    }
  } else {
    return orderItem;
  }
};

export const getQuantityStep = (orderItem: OrderItemCreateInterface, configuration: ConfigurationInterface): number => {
  const hasPackingList = orderItem?.packings?.length > 0;
  const allowMultiple = configuration?.allowsMultipleWithoutPackaging;

  if (hasPackingList && !allowMultiple) {
    return 1;
  }

  if (orderItem?.multiplesSubsidiary?.subsidiaryExternalCode) {
    return orderItem.multiplesSubsidiary.quantityMultiple ?? 1;
  }

  if (orderItem?.quantityMultiple) {
    return orderItem.quantityMultiple;
  }

  return 1;
};

export const validateAndCorrectQuantity = (
  orderItem: OrderItemCreateInterface,
  configuration: ConfigurationInterface
): { isValid: boolean; correctedQuantity: number; message?: any } => {
  const currentQty = Number(orderItem?.soldQuantity || 0);
  if (currentQty === 0) {
    return { isValid: true, correctedQuantity: 0 };
  }

  const step = getQuantityStep(orderItem, configuration);
  const factor = 100000000;
  const intQty = Math.round(currentQty * factor);
  const intStep = Math.round(step * factor);
  const isMultiple = intQty % intStep === 0;
  const isAboveMin = currentQty >= step;

  if (isMultiple && isAboveMin) {
    return { isValid: true, correctedQuantity: currentQty };
  }

  const correctedQuantity = Math.ceil(intQty / intStep) * step;

  const message = {
    id: 'orderWarningMultipleSubsidiaryProductQuantityMultiple',
    values: {
      quantityMultiple: step,
      newSoldQuantity: correctedQuantity
    }
  };

  return {
    isValid: false,
    correctedQuantity: correctedQuantity,
    message
  };
};

export const getDecimalPlaces = (stepQuantity: string | number) => {
  const str = String(stepQuantity);
  if (str.includes('e-')) return parseInt(str.split('e-')[1], 10);
  return str.includes('.') ? str.split('.')[1].length : 0;
};

export const calculateTypeOperation = (total: number, type = 'M', unit = 1, configuration: ConfigurationInterface) => {
  const totalValue =
    type === 'M'
      ? toDecimals(unit * total, configuration?.decimals)
      : toDecimals(total / unit, configuration?.decimals);

  return totalValue;
};

export const getTotalsKit = (orderItems: any[], promotion) => {
  let productsInKit = [];

  let totalSoldPriceKit = 0;
  let promotionKitQuantity = promotion?.promotionKitQuantity ?? 1;
  let promotionDiscountPercentage = promotion?.discountPercentage ?? 0;

  for (const productInKitList of promotion?.productExternalCodes) {
    const product = orderItems?.find(item => item?.externalCode === productInKitList?.productExternalCode);
    let soldPrice = productInKitList?.fixedPrice
      ? productInKitList?.fixedPrice
      : product?.soldPrice ?? product?.baseValue;

    if (product) {
      const totalPriceProductKit = productInKitList?.fixedPrice
        ? productInKitList?.fixedPrice * productInKitList?.minimalQuantity
        : (soldPrice ?? product?.baseValue) * productInKitList?.minimalQuantity;

      productsInKit = [
        ...productsInKit,
        {
          ...product,
          quantityOfProductInKit: productInKitList?.minimalQuantity,
          totalPriceProductKit,
          soldPrice:
            promotionDiscountPercentage > 0 ? soldPrice - (soldPrice * promotionDiscountPercentage) / 100 : soldPrice
        }
      ];
    }
  }

  totalSoldPriceKit = productsInKit?.reduce(
    (total, item) => (total += (item.soldPrice ?? item?.baseValue) * (item?.quantityOfProductInKit ?? 1)),
    0
  );

  return {
    totalSoldPriceKit,
    productsInKit,
    promotionKitQuantity
  };
};

export const meProductRestrictions = (me: any) => {
  Object.keys(me?.user?.productRestrictions).forEach(
    key => me?.user?.productRestrictions[key].length === 0 && delete me?.user?.productRestrictions[key]
  );

  Object.keys(me?.team?.productRestrictions).forEach(
    key => me?.team?.productRestrictions[key].length === 0 && delete me?.team?.productRestrictions[key]
  );

  return me;
};

export const getMarginTotals = ({ allowProfitabilityMarkup, total, financialCost, precision = 2, orderItems = [] }) => {
  const marginOrder = orderItems.reduce(
    (acc, cur) => {
      return {
        financialCost: acc.financialCost + toDecimals(cur.financialCost * cur.soldQuantity, precision),
        total: acc.total + toDecimals(cur.soldPrice * cur.soldQuantity, precision)
      };
    },
    {
      financialCost,
      total
    }
  );

  return {
    marginPercentage: toDecimals(
      allowProfitabilityMarkup
        ? (marginOrder.total < marginOrder.financialCost ? -1 : 1) *
            (marginOrder.total / marginOrder.financialCost) *
            100
        : ((marginOrder.total - marginOrder.financialCost) / marginOrder.total) * 100,
      precision
    ),
    marginValue: toDecimals(marginOrder.total - marginOrder.financialCost, precision)
  };
};

export function* validateProductListBonification({
  orderItems,
  orderTypeProps,
  removeOnRecalculate = false,
  subsidiary
}) {
  const user: UserInterface = yield select((state: AppStateType) => state?.auth?.user);
  const { orderType: stateOrderType } = yield select((state: AppStateType) => state.order?.form?.data);

  let orderType: 'all' | 'sale' | 'bonification' = orderTypeProps
    ? orderTypeProps.type === 'normal'
      ? 'sale'
      : orderTypeProps.type
    : stateOrderType.type === 'normal'
    ? 'sale'
    : stateOrderType.type;

  const parsedOrderItems = Array.isArray(orderItems) ? orderItems : [orderItems];

  const params = {
    orderType,
    subsidiaryExternalCode: subsidiary?.externalCode,
    orderItems: parsedOrderItems.map(item => ({
      productExternalCode: item?.externalCode,
      productName: item?.name,
      categoryExternalCode: item?.categoryExternalCode,
      sectionExternalCode: item?.sectionExternalCode,
      providerExternalCode: item?.providerExternalCode,
      brandExternalCode: item?.brandExternalCode,
      productLineExternalCode: item?.productLineExternalCode,
      userExternalCode: user?.externalCode,
      team: user?.team?.externalCode,
      superiorExternalCode: user?.superiorExternalCode
    }))
  };

  const { data: productListBonificationRestriction }: { data: ProductListBonificationRestrictionResponse[] } =
    yield call(orderService.getRestrictionBonificationProductCheck, {
      params
    });

  if (productListBonificationRestriction.length > 0) {
    const isBlocked = parsedOrderItems?.some(orderItem =>
      productListBonificationRestriction.some(product => orderItem?.externalCode === product?.productExternalCode)
    );

    if (isBlocked) {
      const blockedProduct = productListBonificationRestriction.find(product => {
        return parsedOrderItems?.some(orderItem => orderItem?.externalCode === product?.productExternalCode);
      });

      yield call(
        notification.warning,
        formatMessage('orderWarningProductBonificationRestriction', {
          productExternalCode: blockedProduct?.productExternalCode,
          description:
            blockedProduct?.restriction === 'all'
              ? 'todos'
              : blockedProduct?.restriction === 'bonification'
              ? 'bonificação'
              : 'venda'
        })
      );

      return true;
    }

    return false;
  }
}

export function groupIntoChunks<T>(array: T[], size: number): T[][] {
  const chunks: T[][] = [];
  for (let i = 0; i < array.length; i += size) {
    chunks.push(array.slice(i, i + size));
  }
  return chunks;
}

export function updatePendencies(pendencies, update) {
  return {
    ...pendencies,
    isBlock: pendencies?.isBlock || update?.isBlock,
    messages: {
      ...pendencies?.messages,
      ...update?.messages
    },
    notices: {
      ...pendencies?.notices,
      ...update?.notices
    },
    types: [...pendencies?.types, ...update?.types]
  };
}
export async function getLastOrderProductDetails(clientExternalCode: string, productExternalCode: string) {
  try {
    const { data } = await api.get('/order/v2/last-order-product-details', {
      params: { clientExternalCode, productExternalCode }
    });
    return data;
  } catch (e) {
    return null;
  }
}

export function checkBlockOrder({
  acceptBlockedSale = false,
  acceptNegativeBalance = false,
  acceptSaleWithoutStock = false,
  errorTypes = [],
  isBlock = false,
  isBudget = false,
  priceDifference = 0
}: CheckBlockOrderInterface): boolean {
  if (isBudget || !isBlock) {
    return false;
  }

  if (
    errorTypes.some(
      error =>
        error === 'invalidCombo' ||
        error === 'hasItemsWithZeroQuantity' ||
        error === 'invalidExpiryBatch' ||
        error.startsWith('hasItemsBelowMininumPriceInPromotion') ||
        error.startsWith('hasItemsRequiredByPromotion')
    )
  ) {
    return true;
  }

  if (!acceptNegativeBalance && priceDifference < 0) {
    return true;
  }

  if (!acceptSaleWithoutStock && errorTypes.includes('acceptSaleWithoutStock')) {
    return true;
  }
  errorTypes = errorTypes.filter(error => error !== 'acceptSaleWithoutStock');

  if (!acceptBlockedSale && errorTypes.length > 0) {
    return true;
  }

  return false;
}

export function checkBlockComboKit(orderItems: OrderItemCreateInterface[]) {
  const combos = orderItems?.filter(
    item =>
      (item?.promotion?.isPromotionKit || item?.promotion?.promotion?.isPromotionKit) &&
      (item?.promotion?.requireAdditionalProductToKit || item?.promotion?.promotion?.requireAdditionalProductToKit)
  );

  return (
    combos?.some(combo => {
      const promotion = combo?.promotion?.promotion || combo?.promotion;
      const comboId = promotion?.aggregatorExternalCode;
      const hasOtherProduct = orderItems.some(item => {
        const itemPromotionId =
          item?.promotion?.promotion?.aggregatorExternalCode || item?.promotion?.aggregatorExternalCode;
        return itemPromotionId !== comboId;
      });

      return !hasOtherProduct;
    }) ?? false
  );
}
export async function validateBatch({
  subsidiaryExternalCode,
  orderItem,
  minBatchExpiryDays,
  notify = true
}: {
  subsidiaryExternalCode: string;
  orderItem: OrderItemCreateInterface;
  minBatchExpiryDays: number;
  notify?: boolean;
}) {
  const defaultBatch = {
    isValidBatch: true,
    totalAvailableInValidBatches: orderItem?.soldQuantity
  };
  if (minBatchExpiryDays === null || minBatchExpiryDays === undefined) return defaultBatch;

  const today = new Date();
  const limitDate = new Date();
  limitDate.setDate(today.getDate() + minBatchExpiryDays);
  let productBatch;
  try {
    const { data } = await productService.getBatchValityList({
      subsidiaryExternalCode: subsidiaryExternalCode,
      productExternalCode: orderItem?.externalCode
    });
    productBatch = data;
  } catch {
    return defaultBatch;
  }
  if (productBatch?.length === 0) return defaultBatch;

  const validBatches =
    productBatch?.filter(batch => {
      const expiryDate = new Date(batch?.expirationDate);
      return expiryDate >= limitDate;
    }) || [];

  const totalAvailableInValidBatches = validBatches.reduce((acc, batch) => acc + (batch?.quantity || 0), 0);
  if (notify && orderItem.soldQuantity > totalAvailableInValidBatches) {
    notification.error(
      formatMessage('orderDetailBlockBatch', {
        productName: orderItem?.name,
        minBatchExpiryDays,
        totalAvailableInValidBatches
      })
    );
  }
  return {
    isValidBatch: orderItem?.soldQuantity <= totalAvailableInValidBatches,
    totalAvailableInValidBatches
  };
}

export function setPaymentOptionsAndPlans({
  bonification = false,
  paymentOptions = [],
  paymentPlans = [],
  paymentOptionExternalCode = null,
  paymentPlanExternalCode = null,
  usePaymentPlansGroup = false,
  paymentPlanGroup1ExternalCode = null,
  paymentPlanGroup2ExternalCode = null
}: SetPaymentOptionsAndPlansInterface) {
  return {
    paymentOption: bonification
      ? paymentOptions.find(item => item?.externalCode === 'BNF')
      : paymentOptions.find(item => item.externalCode === paymentOptionExternalCode),
    paymentPlan: bonification
      ? paymentPlans.find(item => item.termType === 'B')
      : paymentPlans.find(item => item.externalCode === paymentPlanExternalCode) ?? null,
    paymentPlanGroup: usePaymentPlansGroup
      ? {
          paymentPlan1: paymentPlans.find(item => item.externalCode === paymentPlanGroup1ExternalCode),
          paymentPlan2: paymentPlans.find(item => item.externalCode === paymentPlanGroup2ExternalCode)
        }
      : {
          paymentPlan1: null,
          paymentPlan2: null
        }
  };
}

export async function checkRequiredBasket(
  order: OrderCreateInterface,
  orderTypes: OrderTypeInterface[]
): Promise<boolean> {
  if (order?.orderType?.type === 'basket') return true;
  const basketTypes = orderTypes?.filter(type => type.type === 'basket' && type?.requiredBasket === true);

  if (!basketTypes || basketTypes.length === 0) {
    return true;
  }

  const currentSubsidiary = order?.orderDetails?.subsidiary?.externalCode;
  const isSubsidiaryRequired = basketTypes.some(
    basket => basket.subsidiaries?.includes(currentSubsidiary) || basket?.subsidiaries?.length === 0
  );

  if (isSubsidiaryRequired) {
    try {
      const { data } = await orderService.getRequiredBasket(order?.client?.externalCode, currentSubsidiary);

      if (!data?.orderAlreadySent) {
        return false;
      }
    } catch (error) {
      return false;
    }
  }

  return true;
}

export function checkProductIsBelowMinimumPrice({
  isPromotionKit,
  soldPrice,
  minimumPrice,
  acceptNegativeBalance,
  decimals = 2
}: {
  isPromotionKit: boolean;
  soldPrice: number;
  minimumPrice: number;
  acceptNegativeBalance: boolean;
  decimals?: number;
}): boolean {
  if (isPromotionKit || acceptNegativeBalance) {
    return false;
  }
  return comparePrices(soldPrice, minimumPrice, decimals);
}

export function checkProductIsAboveDiscountAllowed({
  discountPercentage = 0, // Valor padrão para evitar undefined
  promotionTypeBalance,
  totalDiscount,
  promotionMaxDiscountPercentage,
  maxDiscountTotal,
  acceptNegativeBalance = false
}: {
  discountPercentage?: number;
  promotionTypeBalance?: boolean | null;
  totalDiscount: number;
  promotionMaxDiscountPercentage: number;
  maxDiscountTotal: number;
  acceptNegativeBalance?: boolean;
}): boolean {
  if (acceptNegativeBalance) {
    return false;
  }

  if (promotionTypeBalance) {
    return discountPercentage > totalDiscount;
  }

  if (promotionMaxDiscountPercentage) {
    return discountPercentage > promotionMaxDiscountPercentage;
  }

  return discountPercentage > maxDiscountTotal;
}

export const calculateTotalMaxDiscount = ({
  applyCombinedDiscount,
  applySequentialDiscountsForMinimumPrice,
  promotionDiscount,
  maxDiscount,
  baseValue = 0,
  decimals = 2
}: {
  applyCombinedDiscount?: boolean | null;
  applySequentialDiscountsForMinimumPrice?: boolean | null;
  promotionDiscount?: number | null;
  maxDiscount?: number | null;
  baseValue?: number | null;
  decimals?: number | null;
}): number => {
  const safePromotionDiscount = promotionDiscount ?? 0;
  const safeMaxDiscount = maxDiscount ?? 0;
  const safeBaseValue = baseValue ?? 0;

  if (applySequentialDiscountsForMinimumPrice && safeBaseValue > 0) {
    const valueAfterFirstDiscount = safeBaseValue * (1 - safePromotionDiscount / 100);
    const finalValue = valueAfterFirstDiscount * (1 - safeMaxDiscount / 100);
    const totalDiscountAmount = safeBaseValue - finalValue;
    return toDecimals((totalDiscountAmount / safeBaseValue) * 100, decimals);
  }

  if (applyCombinedDiscount) {
    return safePromotionDiscount + safeMaxDiscount;
  }

  return safePromotionDiscount || safeMaxDiscount;
};
